import { delay } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import * as onlineStoreApi from 'api/onlineStore';
import {
  ONLINE_STORE_START_FACEBOOK_SIGN_IN_FLOW,
  ONLINE_STORE_PATCH_META_TAG,
  ONLINE_STORE_SHOW_FACEBOOK_DOMAIN_VERIFICATION_MODAL,
} from 'actions/onlineStore/constants';
import * as onlineStoreAction from 'actions/onlineStore';
import * as action from 'actions';
import * as actionNotification from 'actions/notifications';
import * as fbClevertap from 'clevertap/onlineStore/facebook';
import {
  CONNECT_FB_SOURCE,
  ONLINE_STORE_CHANNEL,
} from 'utils/onlineStoreUtils';

const fbConnectClevertapMap = {
  [CONNECT_FB_SOURCE.LANDING_PAGE]: {
    success: fbClevertap.fbConnectAccountSuccess,
    failure: fbClevertap.fbConnectAccountFailure,
  },
  [CONNECT_FB_SOURCE.OUTLET_LIST_PAGE]: {
    success: fbClevertap.fbConnectAnotherOutletSuccess,
    failure: fbClevertap.fbConnectAnotherOutletFailure,
  },
  [CONNECT_FB_SOURCE.CONTINUE_SETUP]: {
    success: fbClevertap.fbContinueSetupSuccess,
    failure: fbClevertap.fbContinueSetupFailure,
  },
};

function* fetchData(api, ...params) {
  try {
    yield put(action.showLoadingSpinner(true));
    const response = yield call(api, ...params);
    return [response.data, null];
  } catch (error) {
    yield put(actionNotification.error(error.message));
    return [null, error.message];
  } finally {
    yield put(action.showLoadingSpinner(false));
  }
}

function loginFB(shop, outletName, outletInfo) {
  const { FB } = window;

  return new Promise(resolve => {
    FB.login(resolve, {
      scope:
        'catalog_management,manage_business_extension,instagram_basic,pages_read_engagement,ads_management,business_management',
      extras: {
        setup: {
          external_business_id: shop.id,
          timezone: 'Asia/Jakarta',
          currency: 'IDR',
          business_vertical: 'ECOMMERCE',
          domain: outletInfo.go_store_brand_page_url,
          channel: 'COMMERCE_OFFSITE',
        },
        business_config: {
          business: {
            name: outletName,
          },
        },
        repeat: false,
      },
    });
  });
}

function* pollShopStatus(shopId) {
  try {
    yield put(action.showLoadingSpinner(true));
    const startTime = +new Date();

    while (true) {
      const { data } = yield call(onlineStoreApi.getShopById, {
        shopId,
      });

      if (data.status === 'ACTIVE' || +new Date() - startTime >= 6000) {
        return [data, null];
      }

      yield delay(2000, true);
    }
  } catch (error) {
    yield put(actionNotification.error(error.message));
    return [null, error.message];
  } finally {
    yield put(action.showLoadingSpinner(false));
  }
}

function* startFacebookSignInFlow({ payload }) {
  const { outletId, outletName, source } = payload;
  let { shop, outletInfo } = payload;
  const fbConnectClevertap = fbConnectClevertapMap[source];
  const isModalOpened = yield select(
    state => state.onlineStore.facebook.isModalOpened
  );

  if (!window.FB || isModalOpened) {
    return;
  }

  yield put(onlineStoreAction.facebookModalOpened());

  const [businessInfo, err] = yield fetchData(onlineStoreApi.getBusinessInfo);
  if (err) {
    fbConnectClevertap.failure(err);
    yield put(onlineStoreAction.facebookModalClosed());
    return;
  }
  yield put(onlineStoreAction.setBusinessInfo(businessInfo));

  if (!shop) {
    const [data, error] = yield fetchData(
      onlineStoreApi.createShop,
      ONLINE_STORE_CHANNEL.FACEBOOK,
      outletId
    );

    if (error) {
      fbConnectClevertap.failure(error);
      yield put(onlineStoreAction.facebookModalClosed());
      return;
    }

    shop = data;
  }

  if (!outletInfo) {
    const [data, error] = yield fetchData(onlineStoreApi.getOutlet, outletId);

    if (error) {
      fbConnectClevertap.failure(error);
      yield put(onlineStoreAction.facebookModalClosed());
      return;
    }

    outletInfo = data;
  }

  let fbConnected = false;
  let pageName = outletName;
  const loginResponse = yield loginFB(shop, outletName, outletInfo);
  yield put(onlineStoreAction.facebookModalClosed());

  if (loginResponse.authResponse) {
    const [data, error] = yield pollShopStatus(shop.id);

    if (!error) {
      fbConnected = data.status === 'ACTIVE';

      if (data.additional && data.additional.facebook_page_name) {
        pageName = data.additional.facebook_page_name;
      }
    }
  }

  yield put(onlineStoreAction.pageRequest(ONLINE_STORE_CHANNEL.FACEBOOK));

  if (!fbConnected) {
    yield put(onlineStoreAction.closeOutletModal());
    return;
  }

  fbConnectClevertap.success();
  yield put(onlineStoreAction.closeOutletModal());
  yield put(
    actionNotification.success(
      `${pageName} has been successfully connected to Facebook & Instagram Shop.`
    )
  );

  yield put(
    onlineStoreAction.startSyncingProductValidationFlow(shop.id, false)
  );
}

function* patchMetatag({ payload }) {
  const { name, content } = payload;

  try {
    yield put(action.showLoadingSpinner(true));
    yield call(onlineStoreApi.patchMetaTag, name, content);
    yield put(
      onlineStoreAction.onChangeFacebookDomainVerification({
        error: null,
        step: 2,
      })
    );
  } catch (error) {
    yield put(
      onlineStoreAction.onChangeFacebookDomainVerification({
        error: error.message,
        step: 1,
      })
    );
  } finally {
    yield put(action.showLoadingSpinner(false));
  }
}

function* showFacebookDomainVerificationModal({ payload }) {
  const { businessInfo } = payload;

  if (!businessInfo) {
    const businessInfo = yield call(onlineStoreApi.getBusinessInfo);
    yield put(onlineStoreAction.setBusinessInfo(businessInfo.data));
  }
}

export default function* facebookSaga() {
  yield takeEvery(
    ONLINE_STORE_START_FACEBOOK_SIGN_IN_FLOW,
    startFacebookSignInFlow
  );
  yield takeEvery(ONLINE_STORE_PATCH_META_TAG, patchMetatag);
  yield takeEvery(
    ONLINE_STORE_SHOW_FACEBOOK_DOMAIN_VERIFICATION_MODAL,
    showFacebookDomainVerificationModal
  );
}
